import { Card, Box, Checkbox, Typography, TextField, MenuItem, Menu } from "@mui/material";
import { Dispatch, SetStateAction, useState, useEffect } from "react";
import { t } from "i18next";
import { FolderIcon } from "../../icons";
import MouseClickList from "../MouseClickList";
import { deleteFolder, editFolderName, getFolderContents } from "common-lib";
import { toast } from "react-toastify";
import { IDragInfo } from "../../@types";
import { Check, Folder, Info, MoreVertical } from "lucide-react";

interface FolderCardProps {
  folder: FolderItem;
  checkedProjects: number[];
  checkedFolders: number[];
  setCheckedFolders: Dispatch<SetStateAction<number[]>>;
  setCurrentFolder: Dispatch<SetStateAction<FolderItem>>;
  setCheckedProjects: Dispatch<SetStateAction<number[]>>;
  currentPath: string;
  setCurrentPath: Dispatch<SetStateAction<string>>;
  folderList: FolderItem[];
  setFolderList: Dispatch<SetStateAction<FolderItem[]>>;
  onDragStart: (e: React.DragEvent, item: string) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetFolderId: number) => void;
  setOpenFolderInfo: (open: boolean) => void;
}

export default function FolderCard({
  folder,
  checkedProjects,
  checkedFolders,
  setCheckedFolders,
  setCurrentFolder,
  setCheckedProjects,
  currentPath,
  setCurrentPath,
  folderList,
  setFolderList,
  onDragStart,
  onDragEnd,
  onDrop,
  setOpenFolderInfo,
}: FolderCardProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [checked, setChecked] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [folderName, setFolderName] = useState(folder.name);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl || menuAnchor);
  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    setFolderName(folder.name);
    setIsEditing(false);
    handleClose();
  }, [folder, currentPath]);

  useEffect(() => {
    setChecked(checkedFolders.includes(folder.id));
  }, [checkedFolders, folder.id]);

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setChecked((prev) => !prev);

    setCheckedFolders((prev: number[]) => {
      if (prev.includes(folder.id)) {
        return prev.filter((id) => id !== folder.id);
      } else {
        setOpenFolderInfo(true);
        return [...prev, folder.id];
      }
    });
  };

  // 폴더 더블클릭: 경로 진입
  const handleDoubleClick = () => {
    setCurrentFolder(folder);
    if (folder.id !== -1) {
      setCurrentPath(currentPath + "/" + folder.name);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      try {
        const response = await editFolderName(folder.id, folderName);
        setIsEditing(false);
        setFolderList((prevList) =>
          prevList.map((item) => (item.id === folder.id ? { ...item, name: folderName } : item)),
        );
        toast.success(response.message);
      } catch (error: any) {
        toast.error(error.message);
      }
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    setFolderName(folder.name);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    try {
      const response = await getFolderContents(folder.id);
      handleClose();

      if (response.projectList.length > 0) {
        const confirmed = window.confirm(
          `${t("FolderContainsProjectsPrefixMsg")}${response.total}${t("FolderContainsProjectsSuffixMsg")}`,
        );
        if (!confirmed) return;
      }

      const res = await deleteFolder(folder.id);
      toast.success(res.message);

      setFolderList(folderList.filter((item) => item.id !== folder.id));
    } catch (error) {
      console.error(error);
      toast.error(t("DeleteErrorMsg"));
    }
  };

  // 메뉴 열기
  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  // 메뉴 닫기
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAnchor(null);
  };

  // 이름 수정 시작
  const handleStartEdit = () => {
    console.log("edit");
    setIsEditing(true);
    setMenuAnchor(null);
  };

  const handleSave = async () => {
    const trimmed = folderName.trim();
    if (trimmed === "" || trimmed === folder.name) {
      setIsEditing(false);
      setFolderName(folder.name);
      return;
    }
    if (folder.name !== folderName) {
      await editFolderName(folder.id, trimmed);
      setFolderList((prev) => prev.map((item) => (item.id === folder.id ? { ...item, name: trimmed } : item)));
    }

    setIsEditing(false);
  };

  return (
    <>
      <div
        className="group relative"
        style={{
          background: "var(--dk-surface, #fff)",
          borderRadius: "10px",
          border: `1px solid ${hovered ? "#fed7aa" : "#e5e7eb"}`,
          boxShadow: hovered ? "0 4px 16px rgba(249,115,22,0.08)" : "none",
          transform: hovered ? "translateY(-1px)" : "none",
          transition: "all .18s",
          cursor: "pointer",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onContextMenu={handleContextMenu}
        onClick={handleCheckboxClick}
        onDoubleClick={handleDoubleClick}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => onDrop(e, folder.id)}
        onDragStart={(e) => onDragStart(e, folder.name)}
        onDragEnd={onDragEnd}
      >
        <div style={{ padding: "14px" }}>
          {/* 체크박스 + 더보기 */}
          {(hovered || checked) && !isEditing && (
            <Box
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Checkbox
                checked={checked}
                onClick={handleCheckboxClick}
                size="small"
                sx={{ p: "2px", color: "var(--dk-text-3, #d1d5db)", "&.Mui-checked": { color: "#f97316" } }}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e);
                }}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "5px",
                  border: "1px solid transparent",
                  background: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all .15s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "#f3f4f6";
                  (e.currentTarget as HTMLElement).style.borderColor = "#e5e7eb";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "transparent";
                  (e.currentTarget as HTMLElement).style.borderColor = "transparent";
                }}
              >
                <MoreVertical size={13} style={{ color: "var(--dk-text-2, #6b7280)" }} />
              </button>
            </Box>
          )}

          {/* 아이콘 */}
          <div style={{ marginBottom: "10px" }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Folder size={16} style={{ color: "#f97316" }} />
            </div>
          </div>

          {/* 이름 or 편집 */}
          {isEditing ? (
            <div
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <TextField
                value={folderName}
                autoFocus
                onChange={(e) => setFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                }}
                size="small"
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "13px",
                    borderRadius: "7px",
                    height: "32px",
                    "& fieldset": { borderColor: "#fed7aa" },
                    "&.Mui-focused fieldset": { borderColor: "#f97316" },
                  },
                }}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  border: "1px solid #bbf7d0",
                  background: "#f0fdf4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Check size={13} style={{ color: "#16a34a" }} />
              </button>
            </div>
          ) : (
            <p
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "var(--dk-text, #111827)",
                margin: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {folder.name}
            </p>
          )}
        </div>

        <Menu
          anchorEl={anchorEl || menuAnchor}
          open={open}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              background: "var(--dk-surface, #fff)",
              border: "1px solid var(--dk-border, #e5e7eb)",
              borderRadius: "10px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              "& .MuiMenuItem-root": {
                fontSize: 13,
                color: "var(--dk-text-2, #374151)",
                borderRadius: "6px",
                mx: "4px",
                my: "2px",
                "&:hover": { background: "var(--dk-surface-2, #f9fafb)" },
              },
            },
          }}
        >
          <MenuItem onClick={handleStartEdit}>{t("EditFolderName")}</MenuItem>
          <MenuItem
            onClick={() => {
              handleMenuClose();
              handleDelete();
            }}
            sx={{ color: "#ef4444 !important" }}
          >
            {t("Delete")}
          </MenuItem>
        </Menu>
      </div>
    </>
  );
}
